import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAnalysisResult } from 'src/presentation/types/interfaces/IAnalysisResult.interface';

import { Flex } from '@/lib/v2/components/Layout/Stack';
import { Text } from '@/lib/v2/components/Text';
import { StatsCards, StatsProps } from '@/lib/v2/examples/StatsCards/StatsCards';
import { CheckSimple } from '@/lib/v2/icons/solid';

import { useStateFileContext } from '@/src/ContactsModule/contexts/ImportFileContext';
import { getDataCard } from '@/src/ContactsModule/contexts/ImportFileContext/DataCard';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';

import { ProgressCard, ProgressCardProps } from './ProgressCard';

export const ContactsViewInfo = ({
  analysisResult,
}: {
  analysisResult: IAnalysisResult | undefined;
}) => {
  const { files } = useStateFileContext();
  const [showProgressCard, setShowProgressCard] = useState<boolean>(
    files?.showProgressCard ?? true
  );
  const { t } = useTranslation();

  const progressCard: ProgressCardProps = {
    title: t('IMPORT_MAIN.importingList'),
    dataCard: getDataCard(),
    setHiddenProgressCard: () => {
      analysisResult && setShowProgressCard(false);
    },
    status: files?.state as loadingStatus,
    step: files?.importLoading?.step as number,
    percentage: files?.importLoading?.percentage as number,
    mode: files?.importLoading?.mode as number,
  };

  const importState = [
    {
      quantity: analysisResult?.detectedContacts,
      state: t('IMPORT_MAIN.processed'),
    },
    {
      quantity:
        analysisResult?.importedContacts.detail.newCorrected !== undefined
          ? analysisResult?.importedContacts.detail.newCorrected +
            analysisResult?.importedContacts.detail.new
          : undefined,
      state: t('IMPORT_MAIN.news'),
    },
    {
      quantity: analysisResult?.importedContacts.detail.updated,
      state: t('IMPORT_MAIN.updated'),
    },
    {
      quantity: analysisResult?.quantityDiscarded.quantity,
      state: t('IMPORT_MAIN.notImported'),
    },
  ] as Array<StatsProps>;

  const stateValidation: string[] = useMemo(() => {
    return [
      t('IMPORT_MAIN.fileReading'),
      t('IMPORT_MAIN.invalidPattern'),
      t('IMPORT_MAIN.syntaxCorrection'),
      t('IMPORT_MAIN.duplicateEmails'),
      t('IMPORT_MAIN.debugSpamTraps'),
      t('IMPORT_MAIN.insecure'),
    ];
  }, [t]);

  return (
    <Flex column>
      {!showProgressCard ? (
        <Flex withGap gapSize="medium">
          <StatsCards stats={importState}>
            <Flex withGap gapSize="medium" itemAlignment="start">
              <Flex column withGap gapSize="medium" itemAlignment="start">
                <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                  <div className="pb-4">
                    <Text className="text-emblueBlue-dark" fontWeight="medium">
                      {t('IMPORT_MAIN.validationHasBeen')}
                    </Text>
                  </div>
                  {stateValidation.map((state) => (
                    <Flex key={state} withGap alignment="start" gapSize="small">
                      <CheckSimple />
                      <Text>{state}</Text>
                    </Flex>
                  ))}
                </div>
              </Flex>
              <Flex column withGap gapSize="small" itemAlignment="start">
                <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                  <div className="pb-4">
                    <Text className="text-emblueBlue-dark" fontWeight="medium">
                      {t('IMPORT_MAIN.validationHasBeen')}
                    </Text>
                  </div>
                  <Flex withGap gapSize="medium">
                    <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                      <Text>{analysisResult?.importedContacts.detail.new}</Text>
                      <Text>{analysisResult?.importedContacts.detail.newCorrected}</Text>
                    </div>
                    <Flex column itemAlignment="start">
                      <Text>{t('IMPORT_MAIN.news')}</Text>
                      <Text>{t('IMPORT_MAIN.newFixed')}</Text>
                    </Flex>
                  </Flex>
                </div>
              </Flex>
              <Flex column withGap gapSize="small" itemAlignment="start">
                <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                  <div className="pb-4">
                    <Text className="text-emblueBlue-dark" fontWeight="medium">
                      {t('IMPORT_MAIN.validationHasBeen')}
                    </Text>
                  </div>
                  <Flex withGap gapSize="medium">
                    <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                      <Text>{analysisResult?.importedContacts.detail.updated}</Text>
                      <Text>{analysisResult?.importedContacts.detail.updatedCorrected}</Text>
                      <Text>{analysisResult?.importedContacts.detail.reactivated}</Text>
                      <Text>{analysisResult?.importedContacts.detail.reactivatedCorrected}</Text>
                      <Text>{analysisResult?.importedContacts.detail.noModifications}</Text>
                    </div>
                    <Flex column itemAlignment="start">
                      <Text>{t('IMPORT_MAIN.updated')}</Text>
                      <Text>{t('IMPORT_MAIN.updatedCorrected')}</Text>
                      <Text>{t('IMPORT_MAIN.reactivated')}</Text>
                      <Text>{t('IMPORT_MAIN.reactivatedCorrected')}</Text>
                      <Text>{t('IMPORT_MAIN.noModification')}</Text>
                    </Flex>
                  </Flex>
                </div>
              </Flex>
              <Flex column withGap gapSize="small" itemAlignment="start">
                <div className="min-h-[264px] w-full bg-[#F4F5FA] p-8">
                  <div className="pb-4">
                    <Text className="text-emblueBlue-dark" fontWeight="medium">
                      {t('IMPORT_MAIN.validationHasBeen')}
                    </Text>
                  </div>
                  <Flex withGap gapSize="medium">
                    <div className="flex h-full w-fit grow flex-col items-end justify-between text-center">
                      <Text>{analysisResult?.quantityDiscarded.detail.repeated}</Text>
                      <Text>{analysisResult?.quantityDiscarded.detail.insufficient}</Text>
                      <Text>{analysisResult?.quantityDiscarded.detail.invalids}</Text>
                      <Text>{analysisResult?.quantityDiscarded.detail.garbage}</Text>
                    </div>
                    <Flex column itemAlignment="start">
                      <Text>{t('IMPORT_MAIN.repeated')}</Text>
                      <Text>{t('IMPORT_MAIN.insufficient')}</Text>
                      <Text>{t('IMPORT_MAIN.invalid')}</Text>
                      <Text>{t('IMPORT_MAIN.garbage')}</Text>
                    </Flex>
                  </Flex>
                </div>
              </Flex>
            </Flex>
          </StatsCards>
        </Flex>
      ) : (
        <div className="w-1/2 py-8">
          <ProgressCard progressCard={progressCard} />
        </div>
      )}
    </Flex>
  );
};
